/**
 * «Ссылка на комнату» (Антон 16.10 10:33) и экран «комната по ссылке».
 *
 * RoomLinkDialog — окно со ссылкой: поле только для чтения, «Копировать»
 * (clipboard + тост «Ссылка скопирована») и «Поделиться» (нативный мост
 * оболочки или Web Share; кнопка рисуется только если путь существует —
 * кнопку-обманку не рисуем, см. share.ts).
 *
 * RoomLinkGate — что видит человек, открывший ссылку на комнату, в которой
 * он НЕ состоит: «Войти в комнату», если правило входа это позволяет,
 * иначе «Комната закрытая, попросите приглашение». Knock не отправляем.
 */
import { useEffect, useRef, useState } from "react";
import type { MatrixClient, Room } from "matrix-js-sdk";

import { useT, useTF } from "../i18n";
import { humanError } from "../matrix/roomActions";
import {
  gateActionFor,
  joinFromLink,
  joinRuleOf,
  previewRoomFromLink,
  roomLinkAlias,
  type LinkPreview,
} from "../matrix/roomLink";
import { Dialog, DialogButton } from "./Dialog";
import { roomShareLink } from "./router";
import { canShareText, shareText } from "./share";
import { s } from "./styles";
import { color, font, radius, space } from "./theme";

/** Скопировать текст: Clipboard API, при отказе — execCommand через textarea. */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* WKWebView/HTTP без прав — пробуем старый путь ниже. */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/** Ссылка для конкретной комнаты (алиас, если есть; иначе id + via). */
export function linkForRoom(room: Room): string {
  return roomShareLink({ roomId: room.roomId, alias: roomLinkAlias(room) });
}

export function RoomLinkDialog({
  client,
  roomId,
  roomName,
  onClose,
}: {
  client: MatrixClient;
  roomId: string;
  roomName: string;
  onClose: () => void;
}) {
  const t = useT();
  const tfmt = useTF();
  const room = client.getRoom(roomId);
  const link = room
    ? linkForRoom(room)
    : roomShareLink({ roomId, alias: null });
  const rule = room ? joinRuleOf(room) : "unknown";
  const action = gateActionFor(rule);
  const [toast, setToast] = useState<"ok" | "fail" | null>(null);
  const timer = useRef<number | null>(null);
  const shareable = canShareText();

  useEffect(() => () => {
    if (timer.current) window.clearTimeout(timer.current);
  }, []);

  const flash = (v: "ok" | "fail") => {
    setToast(v);
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setToast(null), 2500);
  };

  const hint =
    rule === "public"
      ? t("roomLink.openHint")
      : rule === "restricted"
        ? t("roomLink.restrictedHint")
        : action === "knock"
          ? t("roomLink.knockHint")
          : t("roomLink.closedHint");

  return (
    <Dialog
      title={tfmt("roomLink.title", { room: roomName })}
      onClose={onClose}
      footer={
        <>
          <DialogButton onClick={onClose} testId="dialog-cancel">{t("common.close")}</DialogButton>
          {shareable && (
            <DialogButton
              testId="room-link-share"
              onClick={() =>
                void shareText({ text: link, title: roomName }).then((r) => {
                  // «unavailable» — запасной путь: копируем, чтобы нажатие
                  // не оказалось пустым.
                  if (r === "unavailable") void copyText(link).then((ok) => flash(ok ? "ok" : "fail"));
                })
              }
            >
              {t("roomLink.share")}
            </DialogButton>
          )}
          <DialogButton
            kind="primary"
            testId="room-link-copy"
            onClick={() => void copyText(link).then((ok) => flash(ok ? "ok" : "fail"))}
          >
            {t("roomLink.copy")}
          </DialogButton>
        </>
      }
    >
      <div style={{ fontSize: font.small, color: color.textDim }}>{t("roomLink.hint")}</div>
      <label style={{ display: "block", fontSize: font.small, color: color.textDim }}>
        {t("roomLink.fieldLabel")}
        <input
          style={{ ...s.dialogInput, fontFamily: "monospace", fontSize: font.small }}
          value={link}
          readOnly
          tabIndex={-1}
          data-testid="room-link-value"
          data-join-rule={rule}
          onFocus={(e) => e.currentTarget.select()}
          onClick={(e) => e.currentTarget.select()}
        />
      </label>
      <div
        style={{ fontSize: font.micro, color: color.textDim }}
        data-testid="room-link-rule-hint"
      >
        {hint}
      </div>
      {toast && (
        <div
          role="status"
          aria-live="polite"
          data-testid="toast"
          style={{
            alignSelf: "center",
            padding: `${space.sm}px ${space.md}px`,
            borderRadius: radius.md,
            background: toast === "ok" ? color.accent : color.danger,
            color: color.textOnAccent,
            fontSize: font.small,
          }}
        >
          {toast === "ok" ? t("roomLink.copied") : t("roomLink.copyFailed")}
        </div>
      )}
    </Dialog>
  );
}

/**
 * Экран «комната по ссылке» для не-участника. Показывается вместо
 * «Комната недоступна», когда адрес пришёл как ссылка на комнату.
 */
export function RoomLinkGate({
  client,
  target,
  via,
  onJoined,
  onBack,
}: {
  client: MatrixClient;
  /** id («!…») или алиас («#…») из адреса. */
  target: string;
  via?: string[];
  onJoined: (roomId: string) => void;
  onBack: () => void;
}) {
  const t = useT();
  const tfmt = useTF();
  const [preview, setPreview] = useState<LinkPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const viaKey = (via ?? []).join(",");

  useEffect(() => {
    let cancelled = false;
    setPreview(null);
    setError(null);
    void previewRoomFromLink(client, target, via).then((p) => {
      if (!cancelled) setPreview(p);
    });
    return () => {
      cancelled = true;
    };
    // via сравниваем строкой: новый массив на каждый рендер не повод
    // перезапрашивать сервер.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, target, viaKey]);

  const action = preview ? gateActionFor(preview.joinRule) : null;
  const title = preview?.name ?? target;

  return (
    <section style={s.main}>
      <div style={s.centered}>
        <div data-testid="room-link-gate" data-join-rule={preview?.joinRule ?? ""} style={{ maxWidth: 440 }}>
          <p style={{ fontWeight: 600 }}>{title}</p>
          {preview === null ? (
            <p style={{ fontSize: 13, color: color.textDim }}>{t("roomGate.loading")}</p>
          ) : preview.notFound ? (
            <p style={{ fontSize: 13, color: color.textDim }} data-testid="room-link-gate-notfound">
              {t("roomGate.notFound")}
            </p>
          ) : action === "join" ? (
            <>
              {preview.topic && <p style={{ fontSize: 13, color: color.textDim }}>{preview.topic}</p>}
              {preview.members !== null && (
                <p style={{ fontSize: 13, color: color.textDim }}>
                  {tfmt("roomGate.members", { count: preview.members })}
                </p>
              )}
              <button
                style={s.button}
                disabled={busy}
                data-testid="room-link-join"
                onClick={() => {
                  setBusy(true);
                  setError(null);
                  joinFromLink(client, preview.roomId ?? target, via)
                    .then((id) => onJoined(id))
                    .catch((e) => setError(humanError(e)))
                    .finally(() => setBusy(false));
                }}
              >
                {busy ? t("roomGate.joining") : t("roomGate.join")}
              </button>
            </>
          ) : (
            <div data-testid="room-link-gate-closed">
              <p style={{ fontWeight: 600 }}>{t("roomGate.closedTitle")}</p>
              <p style={{ fontSize: 13, color: color.textDim }}>
                {action === "knock" ? t("roomGate.knockBody") : t("roomGate.closedBody")}
              </p>
            </div>
          )}
          {error && (
            <p role="alert" style={{ fontSize: 13, color: color.danger }}>
              {error}
            </p>
          )}
          <p>
            <button style={s.button} onClick={onBack}>{t("error.backToChats")}</button>
          </p>
        </div>
      </div>
    </section>
  );
}
